import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/hooks';
import DevOrgSelector from './DevOrgSelector';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const {
    canUseLocalImpersonation,
    firebaseUser,
    isAuthenticated,
    loading,
    user,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user needs to complete organization setup
  useEffect(() => {
    if (!loading && !canUseLocalImpersonation && firebaseUser && !user && location.pathname !== '/setup-organization') {
      // User is authenticated but doesn't have a user document - needs setup
      navigate('/setup-organization', { replace: true });
    }
  }, [canUseLocalImpersonation, loading, firebaseUser, user, location.pathname, navigate]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f2f2f2'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #f4511e',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (canUseLocalImpersonation) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f2f2f2',
          padding: '24px',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '520px',
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
          }}>
            <h1 style={{ marginTop: 0, marginBottom: '12px' }}>Local Developer Access</h1>
            <p style={{ marginTop: 0, marginBottom: '20px', color: '#555' }}>
              Select an organization to impersonate on localhost. This bypass never applies outside local development.
            </p>
            <DevOrgSelector variant="panel" />
          </div>
        </div>
      );
    }

    return <Navigate to="/login" replace />;
  }

  // Check if user needs organization setup
  if (firebaseUser && !user && location.pathname !== '/setup-organization') {
    return <Navigate to="/setup-organization" replace />;
  }

  // Check if email is verified (only for email/password users)
  // Google users are automatically verified
  if (firebaseUser && !firebaseUser.emailVerified && firebaseUser.providerData[0]?.providerId === 'password') {
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
}

